import { Container, Box, Typography, Button } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import API_Calls_Template from "@/api/calls/political/API_Calls_Template";
import TemplateMapper from "@/components/membership/Template/TemplateMapper";
import TemplateSidebar from "@/components/membership/Template/TemplateSidebar";
import { formatErrorNotification } from "@/components/membership/Template/utils/templateUtils";
import Notifications from "@/components/Notifications/Notifications";
import AreYouSureModal from "@/components/political/Template/AreYouSureModal";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";

function TemplateEditor() {
  const {
    templateData,
    handleTemplateDataChange,
    politicalData,
    resetContext,
  } = useUpdateDataContext();
  const [template, setTemplate] = useState(templateData.template || null);
  const [areYouSureOpen, setAreYouSureOpen] = useState(false);
  const { getMatchingTemplate, saveTemplate, applyTemplate } =
    API_Calls_Template();
  const [isBlankTemplate, setIsBlankTemplate] = useState(false);
  const [notification, setNotification] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMatchingTemplate();
    // fetchMatchingTemplate/getMatchingTemplate are recreated each render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMatchingTemplate = async () => {
    const res = await getMatchingTemplate(
      politicalData.affiliate,
      politicalData.fileCreated,
      politicalData.dataSource,
      true // match order
    );
    if (!res.error && res.data) {
      handleTemplateDataChange({
        name: res.data.name,
        template: res.data.template,
      });
      setTemplate(res.data.template);
      if (res.data.name.includes("-blank")) {
        setIsBlankTemplate(true);
      }
    } else {
      setNotification({
        message: "There was an error fetching this template.",
        severity: "error",
        messageDuration: 7000,
      });
      console.error("Error fetching matched template: ", res.error);
    }
  };

  const handleUpdatedTemplate = (updatedTemplate) => {
    setTemplate(updatedTemplate);
  };

  const confirmFile = async () => {
    setIsProcessing(true);
    handleTemplateDataChange({ template });
    if (templateData.name.endsWith("-default")) {
      processTemplate(templateData.name);
    } else {
      const res = await saveTemplate(
        politicalData.affiliate,
        templateData.name,
        "general",
        politicalData.dataSource,
        templateData.template
      );
      if (res.status === 201) {
        const parsedFilename = parseFilename(res.data?.filename);
        processTemplate(parsedFilename.timestamp);
      } else {
        if (res.message && res.details) {
          setNotification({
            title: res.message,
            message: formatErrorNotification(res.details),
            severity: "error",
            duration: null, // notification will stay open until the user closes it
          });
        } else {
          setNotification({
            message:
              "There was an error processing this file. Please try again.",
            severity: "error",
            duration: 7000,
          });
        }
        setIsProcessing(false);
      }
    }
  };

  const processTemplate = async (templateName) => {
    const data = {
      template: templateName,
      data_file: politicalData.fileCreated,
      data_source: politicalData.dataSource,
    };
    const res = await applyTemplate(politicalData.affiliate, data);
    if (res.status === 201) {
      navigate("/political/update-data/confirmation");
    } else {
      if (res.message && res.details) {
        setNotification({
          title: res.message,
          message: formatErrorNotification(res.details),
          severity: "error",
          duration: null, // notification will stay open until the user closes it
        });
      } else {
        setNotification({
          message: "There was an error processing this file. Please try again.",
          severity: "error",
          duration: 7000,
        });
      }
    }
    setIsProcessing(false);
  };

  const parseFilename = (filename) => {
    if (filename) {
      const parts = filename.split("/");
      return { affiliate: parts.at(1), timestamp: parts.at(3) };
    } else {
      return null;
    }
  };

  const handleOpenModal = () => {
    setAreYouSureOpen(true);
  };

  const handleCloseAreYouSureModal = (submit) => {
    setAreYouSureOpen(false);
    if (submit) {
      confirmFile();
    } else {
      resetContext();
      navigate("/political/update-data/select-file");
    }
  };

  return (
    <>
      <Container
        className="template-editor-container"
        data-testid="template-editor-container"
      >
        {templateData.template &&
          Object.keys(templateData.template).length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "30px 0",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "1.2rem" }}>
                    {politicalData.eventName}
                  </Typography>
                  {isBlankTemplate && (
                    <Box sx={{ color: "red" }}>
                      <Typography>
                        The column names in the file you uploaded don&apos;t
                        match the expected column names.{" "}
                      </Typography>
                      <Typography>
                        You can either manually match the columns or go back and
                        upload a different file.
                      </Typography>
                    </Box>
                  )}
                  {/* <Typography>Template: {templateData.name}</Typography> */}
                </Box>
                <Box>
                  <Button
                    className="siteBlue"
                    onClick={handleOpenModal}
                    disabled={isProcessing}
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
              {isProcessing ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    height: "40px",
                  }}
                >
                  <SiteIcons
                    iconstring="circular-loader"
                    size="2.5rem"
                    page="sitewide-loader"
                  />
                </Box>
              ) : (
                <>
                  <TemplateSidebar template={templateData.template} />
                  <TemplateMapper
                    handleUpdatedTemplate={handleUpdatedTemplate}
                    initialTemplateData={template}
                  />
                </>
              )}
            </>
          )}
        <AreYouSureModal
          open={areYouSureOpen}
          onClose={handleCloseAreYouSureModal}
          dataSource={politicalData.dataSource}
          filename={politicalData.fileName}
        />
      </Container>
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

export default TemplateEditor;
